import React, { useEffect, useState } from 'react';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { useSuperListStore } from './superListStore';

const getConditionPropsForAdvancedFilter = (conditions, defaultConditions) => {
  if (conditions?.operands) {
    let operands = cloneDeep(conditions?.operands);
    operands = operands.filter(
      (operand) => !isEqual(operand, defaultConditions)
    );
    return {
      joinOperator: conditions?.joinOperator,
      operands
    };
  }
  return null;
};

const useSuperListHook = ({
  tableName,
  viewId,
  showOnlyOnAdvancedFilter,
  showOnlyOnTableFilter,
  resetToDefaultAdvanceFilter,
  defaultAdvanceFilter
}) => {
  const {
    setTableCondition,
    setFilterCondition,
    setListInfo,
    getTableCondition,
    getFilterCondition,
    getListInfo,
    getRecords,
    setRecords,
    clearAllTableData,
    clearTableWithViewId,
    resetAllFilters
  } = useSuperListStore();

  const viewIdRef = React.useRef(null);
  viewIdRef.current = viewId;

  const showOnlyOnAdvancedFilterRef = React.useRef(null);
  showOnlyOnAdvancedFilterRef.current = showOnlyOnAdvancedFilter;
  const showOnlyOnTableFilterRef = React.useRef(null);
  showOnlyOnTableFilterRef.current = showOnlyOnTableFilter;

  const currentTableCondition = getTableCondition(tableName, viewIdRef.current);
  const currentFilterCondition = getFilterCondition(
    tableName,
    viewIdRef.current
  );
  const currentListInfo = getListInfo(tableName, viewIdRef.current);
  const currentTableRecords = getRecords(tableName, viewIdRef.current);

  const updateListInfo = (updatedListInfo) => {
    setListInfo(tableName, viewIdRef.current, updatedListInfo);
  };

  const updateRecords = (updatedRecords) => {
    setRecords(tableName, viewIdRef.current, updatedRecords);
  };

  const updateTableCondition = (updatedTableCondition) => {
    // Used to setState for the filter inside the table
    setTableCondition(tableName, viewIdRef.current, updatedTableCondition);
  };

  const updateAdvanceFilter = (updatedFilterCondition) => {
    setFilterCondition(tableName, viewIdRef.current, updatedFilterCondition);
  };

  const updateTableFilterCondition = (
    updatedCondition,
    selectedField,
    ruleName
  ) => {
    const currentTableFilterCondition = getTableCondition(
      tableName,
      viewIdRef.current
    );
    const conditionOperands = currentTableFilterCondition?.operands
      ? cloneDeep(currentTableFilterCondition?.operands)
      : [];
    if (updatedCondition?.operands?.length) {
      updatedCondition?.operands?.forEach((operand) => {
        const presentFilterIndex = conditionOperands.findIndex(
          (el) => el?.attribute === operand?.attribute
        );
        if (presentFilterIndex === -1) {
          conditionOperands.push(operand);
        } else {
          conditionOperands[presentFilterIndex] = operand;
        }
      });

      const isOperandsEmpty = isEmpty(conditionOperands);
      if (isOperandsEmpty) {
        updateTableCondition(null);
      } else {
        updateTableCondition({
          joinOperator: 'AND',
          operands: conditionOperands
        });
      }
    } else {
      const updatedOperands = conditionOperands.filter((el) => {
        const attribute = el?.attribute;
        const selectedFieldName = selectedField?.name;
        return attribute !== selectedFieldName && attribute !== ruleName;
      });
      const isOperandsEmpty = isEmpty(updatedOperands);
      if (isOperandsEmpty) {
        updateTableCondition(null);
      } else {
        updateTableCondition({
          joinOperator: 'AND',
          operands: updatedOperands
        });
      }
    }
  };

  const updateAdvanceFilterCondition = (updatedCondition, appendCondition) => {
    if (updatedCondition) {
      const operands = cloneDeep(updatedCondition?.operands || []);
      if (appendCondition?.operands?.length > 0) {
        const appendConditionData = {
          joinOperator: appendCondition?.joinOperator || 'AND',
          operands: appendCondition?.operands
        };
        operands.push(appendConditionData);
      }
      const joinOperator = updatedCondition?.joinOperator || 'AND';
      const operandsData = operands || null;
      if (isEmpty(operandsData)) {
        updateAdvanceFilter(null);
      } else {
        updateAdvanceFilter({
          joinOperator,
          operands: operandsData
        });
      }
    } else {
      updateAdvanceFilter(null);
    }
  };

  const clearTableDataWithName = () => {
    clearAllTableData(tableName);
  };

  const clearTableDataWithViewId = () => {
    clearTableWithViewId(tableName, viewIdRef.current);
  };

  const resetTableFilters = () => {
    resetAllFilters(
      tableName,
      viewIdRef.current,
      resetToDefaultAdvanceFilter,
      defaultAdvanceFilter
    );
  };

  const updateListInfoProperties = (properties) => {
    if (isEmpty(properties)) return;
    const listInfo = getListInfo(tableName, viewIdRef.current) || {};
    let updatedListInfo = { ...listInfo };

    Object.entries(properties).forEach(([property, value]) => {
      if (property === 'page') {
        updatedListInfo = { ...updatedListInfo, page: value || 1 };
      }
      if (property === 'sort') {
        updatedListInfo = { ...updatedListInfo, sort: value || null };
      }
    });
    updateListInfo(updatedListInfo);
  };

  const getShowResetFilters = () => {
    const hasTableFilters = !!currentTableCondition;
    const hasAdvanceFilters = !!currentFilterCondition;
    if (resetToDefaultAdvanceFilter) {
      const isAdvanceFilterModified = !isEqual(
        currentFilterCondition,
        defaultAdvanceFilter
      );
      return hasTableFilters || isAdvanceFilterModified;
    }
    if (showOnlyOnAdvancedFilterRef.current) {
      return hasAdvanceFilters;
    }

    if (showOnlyOnTableFilterRef.current) {
      return hasTableFilters;
    }
    return hasAdvanceFilters || hasTableFilters;
  };

  const showResetFilter = getShowResetFilters();

  return {
    tableCondition: currentTableCondition,
    advanceFilterCondition: currentFilterCondition,
    listInfo: currentListInfo,
    setListInfo: updateListInfo,
    updateAdvanceFilterCondition,
    updateTableFilterCondition,
    clearTableDataWithName,
    clearTableDataWithViewId,
    records: currentTableRecords,
    setRecords: updateRecords,
    resetFilters: resetTableFilters,
    showResetFilter,
    updateListInfoProperties
  };
};

export default useSuperListHook;
export { getConditionPropsForAdvancedFilter };
